import React, {useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {Formik} from 'formik';
import {useDispatch} from 'react-redux';
import moment from 'moment/moment';
import {shallowEqual, useSelector} from 'react-redux';
import {useTasksUIContext} from '../TasksUIContext';
import {useCitiesContext} from '../../../_common/components/CitiesAndStreets';
import FilterSelect from '../../../_common/components/FilterSelect';
import FilterText from '../../../_common/components/FilterText';
import ClearIcon from '../../../_common/components/ClearIcon';
import {FilterRangePicker} from '../../../_common/components/FieldDatepicker';
import {taskStatuses, taskTypes} from '../../../_common/constants/options';
import {applyFilter} from '../../../../../utils/prepareApplyFilter';
import {generateExcelFile} from '../../../_common/utils/excel';
import {columnNamesTasks} from '../../../_common/constants/excelColumnNames';
import {toAbsoluteUrl} from '../../../../../_metronic/_helpers';
import ButtonAction from '../../../_common/components/ButtonAction';
import {getTasksListForExcel} from '../../actions/TasksTableActions';
import styles from '../../../_common/styles/filtersBar.module.scss';
import classnames from "classnames";

export function TasksFilters({users = []}) {
  const tasksUIContext = useTasksUIContext();
  const tasksUIProps = useMemo(() => {
    return {
      queryParams: tasksUIContext.queryParams,
      setQueryParams: tasksUIContext.setQueryParams,
    };
  }, [tasksUIContext]);
  const {cities} = useCitiesContext();
  const {propertyTypes} = useSelector(
    (state) => ({propertyTypes: state.decisionTreeOptionsReducer.propertyTypes}), shallowEqual
  );
  const [loading, setLoading] = useState(false);

  const {formatMessage} = useIntl();
  const dispatch = useDispatch();
  const prefix = 'tasks-page-filter';

  const qs = new URLSearchParams(window.location.search);
  let taskType = qs.get('taskType') || '';
  let taskStatus = qs.get('taskStatus') || '';
  let cityID = qs.get('cityID') || '';
  let propertyTypeID = qs.get('propertyTypeID') || '';
  let responsibleUserID = qs.get('responsibleUserID') || '';
  let customerName = qs.get('customerName') || '';
  let fromDueDate = qs.get('fromDueDate') || '';
  let toDueDate = qs.get('toDueDate') || '';

  const handleExport = () => {
    setLoading(true);
    dispatch(getTasksListForExcel(tasksUIProps.queryParams))
      .then(prepareDataForExcel)
      .then((data) => {
        generateExcelFile(
          data,
          'Tasks.xlsx',
          'משימות'
        );
      })
      .catch((error) => {
        console.error(`Error: + ${error}`);
      }).finally(() => setLoading(false));
  };
  const prepareDataForExcel = data => {
    return data.map((item, index) => {
      const {
        taskID,
        username,
        firmShumaID,
        propertyTypeName,
        taskType,
        taskStatus,
        customers,
        cityName,
        streetName,
        houseNumber,
        dueDate,
      } = item;

      return {
        [columnNamesTasks[0]]: index + 1,
        [columnNamesTasks[1]]: taskID || '',
        [columnNamesTasks[2]]: username || '',
        [columnNamesTasks[3]]: firmShumaID || '',
        [columnNamesTasks[4]]: propertyTypeName || '',
        [columnNamesTasks[5]]: taskType || '',
        [columnNamesTasks[6]]: taskStatus || '',
        [columnNamesTasks[7]]: customers || '',
        [columnNamesTasks[8]]: [cityName, streetName, houseNumber].filter(Boolean).join(' - '),
        [columnNamesTasks[9]]: dueDate ? moment(dueDate).format('DD-MM-YYYY') : '',
      };
    });
  };

  const onChangeDate = (fromDate, toDate, setFieldValue, handleSubmit) => {
    setFieldValue('fromDueDate', moment(fromDate).format('YYYY-MM-DD') || '');
    setFieldValue('toDueDate', moment(toDate).format('YYYY-MM-DD') || '');
    handleSubmit();
  };
  const clearDate = (setFieldValue, handleSubmit) => {
    setFieldValue('fromDueDate', '');
    setFieldValue('toDueDate', '');
    handleSubmit();
  };

  return (
    <Formik
      initialValues={{
        taskType,
        taskStatus,
        cityID,
        propertyTypeID,
        responsibleUserID,
        customerName,
        toDueDate,
        fromDueDate,
      }}
      onSubmit={(values) => applyFilter(values, tasksUIProps)}
      enableReinitialize={true}
    >
      {({values, handleBlur, handleSubmit, setFieldValue}) => (
        <form onSubmit={handleSubmit} className={styles.container}>
          <div className={styles.filtersContainer}>
            <FilterSelect
              fieldName={'taskType'}
              valueName={values.taskType}
              options={taskTypes}
              placeholder={formatMessage({id: `${prefix}-type`, defaultMessage: 'Type'})}
              containerClassnames={styles.filter}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
            />
            <FilterSelect
              fieldName={'taskStatus'}
              valueName={values.taskStatus}
              options={taskStatuses}
              placeholder={formatMessage({id: `${prefix}-status`, defaultMessage: 'Status'})}
              containerClassnames={styles.filter}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
            />
            <FilterSelect
              fieldName={'cityID'}
              valueName={values.cityID}
              options={cities}
              placeholder={formatMessage({id: 'filter-placeholder-city', defaultMessage: 'City'})}
              containerClassnames={styles.filter}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
            />
            <FilterSelect
              fieldName={'propertyTypeID'}
              valueName={values.propertyTypeID}
              options={propertyTypes}
              placeholder={formatMessage({id: `${prefix}-propertyType`, defaultMessage: 'Property type'})}
              containerClassnames={styles.filter}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
            />
            <FilterSelect
              fieldName={'responsibleUserID'}
              valueName={values.responsibleUserID}
              options={users}
              placeholder={formatMessage({id: `${prefix}-responsibleUser`, defaultMessage: 'Responsible user'})}
              containerClassnames={styles.filter}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
            />
            <FilterText
              fieldName={'customerName'}
              value={values.customerName}
              placeholder={formatMessage({id: `${prefix}-customerName`, defaultMessage: 'Customer name'})}
              containerClassnames={styles.filter}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
              handleSubmit={handleSubmit}
            />
            <div className={classnames(styles.filter, 'w-350px')}>
              <FilterRangePicker
                onChange={(fromDueDate, toDueDate) => onChangeDate(fromDueDate, toDueDate, setFieldValue, handleSubmit)}
                placeholder={[formatMessage({id: `${prefix}-dueDate`, defaultMessage: 'Due date'})]}
                startDate={fromDueDate}
                endDate={toDueDate}
                label={
                  fromDueDate || toDueDate
                    ? formatMessage({id: `${prefix}-dueDate`, defaultMessage: 'Due date'})
                    : null
                }
              />
              <ClearIcon onClick={() => clearDate(setFieldValue, handleSubmit)}/>
            </div>
          </div>
          <div className={styles.buttonsContainer}>
            <ButtonAction
              variant={'success'}
              label={
                <>
                  {loading
                    ? (
                      <div className="spinner-border spinner-border-md m-auto text-light" role="status"/>
                    )
                    : (
                      <>
                        <img
                          src={toAbsoluteUrl('/media/common/Excel_white.svg')}
                          alt="export" className={'mr-2'}
                        />
                        <span>{formatMessage({id: 'button-export-to-excel', defaultMessage: 'Export'})}</span>
                      </>
                    )
                  }
                </>
              }
              onClick={handleExport}
              customClassnames={'w-100px'}
            />
          </div>
        </form>
      )}
    </Formik>
  );
}

export default TasksFilters;